import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ListFilter, X } from 'lucide-react';

import type { ClusterCategoriesResponseOutputItemsItem } from '@/api/generated';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// Draw the item's indicator slot as a checkbox, so the list reads as
// "pick several" even before anything is checked.
const CHECKBOX_ITEM =
  'py-2 pl-9 [&>span:first-child]:left-2.5 [&>span:first-child]:size-4 [&>span:first-child]:rounded-[4px] [&>span:first-child]:border [&>span:first-child]:border-input data-[state=checked]:[&>span:first-child]:border-primary data-[state=checked]:[&>span:first-child]:bg-primary data-[state=checked]:[&>span:first-child]:text-primary-foreground [&>span:first-child_svg]:size-3';

interface ClusterCategoryFilterProps {
  className?: string;
  categories: Array<ClusterCategoriesResponseOutputItemsItem>;
  value: Array<string>;
  onValueChange: (value: Array<string>) => void;
}

export const ClusterCategoryFilter: FC<ClusterCategoryFilterProps> = ({
  className,
  categories,
  value,
  onValueChange,
}: ClusterCategoryFilterProps) => {
  const { t } = useTranslation();
  const toggle = (category: string) => {
    onValueChange(
      value.includes(category)
        ? value.filter((selected) => selected !== category)
        : [...value, category]
    );
  };
  let label = t('clusters.selectedCount', { count: value.length });
  if (value.length === 0) label = t('clusters.allCategories');
  else if (value.length <= 2) label = value.join(', ');

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <ListFilter className='text-muted-foreground size-4' />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid='cluster-category-filter'
            variant='outline'
            size='sm'
            className='min-w-48 justify-between bg-white'
            disabled={categories.length === 0}>
            <span className='truncate'>
              <span className='text-muted-foreground'>
                {t('clusters.category')}:
              </span>{' '}
              {label}
            </span>
            <span className='flex items-center gap-1.5'>
              {value.length > 0 && (
                <span className='bg-primary text-primary-foreground grid size-5 place-items-center rounded-full text-xs'>
                  {value.length}
                </span>
              )}
              <ChevronDown />
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='start'
          className='max-h-[min(32rem,70vh)] w-80'>
          <DropdownMenuLabel className='flex items-baseline justify-between'>
            {t('clusters.category')}
            <span className='text-muted-foreground text-xs font-normal'>
              {t('clusters.pickSeveral')}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {categories.map(({ name: category, remaining }) => (
            <DropdownMenuCheckboxItem
              data-testid='cluster-category-option'
              key={category}
              className={CHECKBOX_ITEM}
              checked={value.includes(category)}
              // Keep the menu open so several categories can be picked in one go.
              onSelect={(event) => {
                event.preventDefault();
              }}
              onCheckedChange={() => {
                toggle(category);
              }}>
              <span className='truncate'>{category}</span>
              {remaining > 0 ? (
                <span className='bg-muted text-muted-foreground ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs font-medium tabular-nums'>
                  {t('clusters.left', { count: remaining })}
                </span>
              ) : (
                <span className='text-success ml-auto flex shrink-0 items-center gap-1 text-xs font-medium'>
                  <Check className='size-3.5' />
                  {t('clusters.categoryDone')}
                </span>
              )}
            </DropdownMenuCheckboxItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid='cluster-category-clear'
            disabled={value.length === 0}
            onSelect={() => {
              onValueChange([]);
            }}>
            <X />
            {t('clusters.clearSelection')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {value.map((category) => (
        <Button
          data-testid='cluster-category-chip'
          key={category}
          variant='secondary'
          size='sm'
          className='h-7 rounded-full'
          aria-label={t('clusters.removeCategory', { category })}
          onClick={() => {
            toggle(category);
          }}>
          {category}
          <X />
        </Button>
      ))}
    </div>
  );
};
